import { z } from "zod";

/**
 * Worker environment contract. Every request re-validates this once via
 * `parseEnv(env)` to catch missing secrets or misconfigured bindings early.
 *
 * NOTE: Cloudflare binding types (D1, Vectorize, AI, Queue, Workflow, DO) are
 * typed as `unknown` here because @pfh/shared is runtime-only — the Worker
 * package refines them against `@cloudflare/workers-types` at its own layer.
 */
export const WorkerEnvZ = z.object({
  ENVIRONMENT: z.enum(["production", "staging", "dev"]).default("production"),

  // Model routing
  CLAUDE_MODEL_HAIKU: z.string().default("claude-haiku-4-5"),
  CLAUDE_MODEL_SONNET: z.string().default("claude-sonnet-4-6"),

  // External endpoints
  FOREXFACTORY_URL: z.string().url(),
  REDIS_PROXY_URL: z.string().url(),
  PROPFIRMMATCH_BASE: z.string().url().default("https://propfirmmatch.com"),
  CF_ACCOUNT_ID: z.string().min(1),

  // PFM scraping strategy — CF Browser Rendering is hard-blocked by
  // PFM's WAF (bot detection ID 119853733 since ~2026-03-17), so we
  // route PFM scrapes through the Playwright bot fleet via BotChannel
  // WS instead. See apps/worker/src/services/intel/fleet-scraper.ts.
  //   "cf"    — legacy path, returns 965 B block page (broken)
  //   "bb"    — Browserbase Tier-2; works when BB isn't WAF-blocked
  //   "fleet" — scrapes via stealth-playwright on local bot fleet
  PFM_SCRAPE_STRATEGY: z.enum(["cf", "bb", "fleet"]).default("fleet"),

  // Secrets (populated via wrangler secret put)
  // Optional until all infrastructure is configured
  ANTHROPIC_API_KEY: z.string().min(1).optional(),
  EA_WEBHOOK_SECRET: z.string().min(16).optional(),
  // Used ONLY for /webhooks/bots/register (bootstrap).
  // After registration, bots sign with their per-bot token from D1.
  BOT_BOOTSTRAP_SECRET: z.string().min(16).optional(),
  // Shared secret between GitHub Actions and this worker for the
  // /webhooks/ci/bot-image-published fan-out. GHA's workflow signs the
  // same HMAC envelope as bot webhooks (`ts + "." + body`). Distinct
  // trust domain from per-bot tokens — never mix.
  CI_WEBHOOK_SECRET: z.string().min(16).optional(),
  REDIS_PROXY_SECRET: z.string().min(16).optional(),
  CF_ACCESS_AUD: z.string().min(1).optional(),
  CF_ACCESS_TEAM: z.string().min(1).optional(),
  CF_BROWSER_RENDERING_TOKEN: z.string().min(1).optional(),
  BETTER_AUTH_SECRET: z.string().min(16).optional(),

  // Browserbase — Tier-2 fallback scraper. Always tried AFTER CF
  // Browser Rendering fails (WAF block page, 4xx/5xx, timeout). Beats
  // Cloudflare WAF-protected sites (propfirmmatch.com) by default,
  // while CF-BR stays primary on every other firm site.
  BROWSERBASE_API_KEY: z.string().min(1).optional(),
  BROWSERBASE_PROJECT_ID: z.string().min(1).optional(),

  // Gate for the PFM account-types write path in ingest.ts. "1" turns on
  // the upsert into firm_profiles.account_types_json + firm_platforms +
  // firm_challenges on every crawl. Defaults to "1" — the crawl output
  // shape was validated against the Playwright backfill on 2026-04-25
  // and is now the authoritative refresh path for these columns. Set to
  // "0" only as an emergency kill-switch (paste-restore from
  // hand-seeded state). See ingest.ts:upsertStructuredFromRules.
  ENABLE_PFM_ACCOUNT_TYPES_UPSERT: z.enum(["0", "1"]).default("1"),
});

export type WorkerEnvShape = z.infer<typeof WorkerEnvZ>;
